import { useCallback, useEffect, useRef, useState } from 'react';
import { API_GET, API_POST, useConfig } from '../../api/api';
import { request } from './aiChatApi';

/**
 * Whether the user has acknowledged the current AI notice (AI Act Art. 50
 * transparency). The server records it per user and refuses writing in the
 * chat until then; the panel shows the notice while `acknowledged` is false.
 * undefined = not known yet.
 */
export function useAiChatAcknowledgement({ active }) {
  const config = useConfig();
  const [acknowledged, setAcknowledged] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!active || !config || loadedRef.current) return;
    loadedRef.current = true;
    request(config, 'ai-chats/acknowledgement', API_GET)
      .then((res) => setAcknowledged(Boolean(res.data?.acknowledged)))
      // unknown stays unknown: the backend refuses writing anyway, see markRequired
      .catch(() => {});
  }, [active, config]);

  const acknowledge = useCallback(async () => {
    setSaving(true);
    try {
      const res = await request(
        config,
        'ai-chats/acknowledgement',
        API_POST({}),
      );
      setAcknowledged(Boolean(res.data?.acknowledged));
    } finally {
      setSaving(false);
    }
  }, [config]);

  /** The backend refused a write because the notice changed: show it again. */
  const markRequired = useCallback(() => setAcknowledged(false), []);

  return { acknowledged, saving, acknowledge, markRequired };
}

/** True for the API error a write gets while the notice is not acknowledged. */
export const isAcknowledgementError = (err) =>
  err?.httpStatus === 403 && err?.data?.reason === 'acknowledgement_required';
